import React, { useCallback, useState, useEffect } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff, Loader2 } from "lucide-react";

// Components & Services
import { notify } from "@shared/tostNotification/notify";
import { BRAND } from "@shared/branding";
import { LoginFormValues, loginSchema } from "@shared/validations/login.schema";
import { ForgotPasswordFormValues, forgotPasswordSchema } from "@shared/validations/forgot.schema";
import { forgotPassword, loginUser } from "@/store/slices/authSlice";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { selectAuthLoading } from "@/store/selectors/authSelectors";

type FormValues = LoginFormValues | ForgotPasswordFormValues;

// Types & Schemas

const LoginForm: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const isLoading = useSelector(selectAuthLoading);
  const [isForgotLoading, setIsForgotLoading] = useState(false);
  const [mode, setMode] = useState<"login" | "forgot">("login");

  const navigate = useNavigate();
  const dispatch = useDispatch<any>();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(mode === "login" ? loginSchema : forgotPasswordSchema),
    defaultValues: {
      email: "",
      password: "",
    },
    mode: "onTouched",
  });

  /** Reset form when mode changes */
  useEffect(() => {
    reset({ email: "", password: "" });
  }, [mode, reset]);

  // Handle form submission
  const onSubmit = useCallback(
    async (data: LoginFormValues) => {
      try {
        const result = await dispatch(
          loginUser({
            email: data.email,
            password: data.password,
          })
        );
        if (loginUser.fulfilled.match(result)) navigate("/dashboard");
        else reset({ password: "" });
      } catch (error: any) {
        console.error("Login error:", error);
        reset({ password: "" });
      }
    },
    [dispatch, navigate, reset]
  );

  const onForgotPassword: SubmitHandler<ForgotPasswordFormValues> = async (data) => {
    try {
      setIsForgotLoading(true);
      const result = await dispatch(forgotPassword({ email: data.email }));

      if (forgotPassword.fulfilled.match(result)) {
        const successMessage = result.payload || 'Password reset link has been sent to your email';
        notify(successMessage, 'success');
      } else if (result.payload) {
        notify(result.payload as string, 'error');
      } else {
        notify('Failed to send password reset link', 'error');
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      notify("Something went wrong. Please try again.", "error");
    } finally {
      setIsForgotLoading(false);
    }
  };

  const isSubmitting = mode === "login" ? isLoading : isForgotLoading;

  return (
    <div className="flex w-full items-center justify-center p-4 sm:p-6 md:w-1/2 md:p-8">
      <div className="w-full max-w-xs rounded-2xl border border-gray-200 bg-white px-4 py-5 shadow-sm sm:max-w-sm sm:rounded-3xl sm:p-6">
        {/* Logo for Mobile */}
        <div className="mb-4 block md:hidden">
          <img src="/goodweek_auth_mobile.svg" alt={BRAND.name || "Logo"} width={150} height={50} />
        </div>

        {/* Heading */}
        <h1 className="mb-4 text-base font-bold text-gray-900 sm:mb-6 sm:text-xl md:text-2xl">
          Log In{BRAND.name ? ` to ${BRAND.name}` : ""}
        </h1>

        <form onSubmit={handleSubmit(mode === "login" ? onSubmit : onForgotPassword)} noValidate>
          {/* Email Field */}
          <div className="mb-3">
            <label className="mb-1 block text-sm font-medium text-[#101828]">
              Email <span className="ml-0.5 text-red-500">*</span>
            </label>

            <input
              type="email"
              autoComplete="email"
              placeholder="Enter your email"
              disabled={isSubmitting}
              {...register("email")}
              className={`h-9 w-full rounded-lg border px-3 text-xs sm:h-10 sm:text-sm
      ${errors.email ? "border-red-300" : "border-gray-300"}
      bg-white text-black focus:border-transparent focus:outline-none
      focus:ring-2 focus:ring-orange-600`}
            />

            {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>}
          </div>

          {/* Password Field */}
          {mode === "login" && (
            <div className="mb-5 sm:mb-6">
              <label className="mb-2 block text-xs font-medium text-gray-700 sm:text-sm">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  className={`h-9 w-full rounded-lg border px-3 py-2 text-xs focus:border-transparent focus:outline-none
                              focus:ring-2 focus:ring-orange-600 sm:h-10 sm:text-sm
                              ${mode === "login" && "password" in errors
                      ? "border-orange-600"
                      : "border-gray-300"
                    }
                                                  ${isLoading ? "bg-gray-50" : ""}
                                                `}
                  placeholder="Enter your password"
                  disabled={isLoading}
                  {...register("password")}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 transform p-1 text-gray-500 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <Eye size={18} aria-hidden="true" />
                  ) : (
                    <EyeOff size={18} aria-hidden="true" />
                  )}
                </button>
              </div>
              {mode === "login" && "password" in errors && (
                <p className="text-xs text-red-500">{errors.password?.message}</p>
              )}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className={`flex w-full items-center justify-center gap-2 rounded-lg py-2.5 text-white transition-all
              ${isSubmitting ? "bg-blue-800 cursor-not-allowed opacity-80" : "bg-blue-900 hover:bg-blue-950"}
            `}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                <span>{mode === "login" ? "Logging in..." : "Sending..."}</span>
              </>
            ) : mode === "login" ? (
              "Login"
            ) : (
              "Send Reset Link"
            )}
          </button>

          <div className="pt-3 text-center">
            {mode === "login" ? (
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => {
                  reset();
                  setMode("forgot");
                }}
                className={`text-xs text-blue-600 hover:underline sm:text-sm ${isSubmitting ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                Forgot Password?
              </button>
            ) : (
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => {
                  reset();
                  setMode("login");
                }}
                className={`text-xs text-blue-600 hover:underline sm:text-sm ${isSubmitting ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                Back to Login
              </button>
            )}
          </div>

          {/* Sign Up Link */}
          <div className="pt-2 text-center">
            <span className="text-xs font-normal text-gray-600 sm:text-sm">
              Don't have an account?{" "}
            </span>
            <button
              type="button"
              onClick={() => navigate("/signup")}
              disabled={isSubmitting}
              className="cursor-pointer rounded text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 sm:text-sm"
            >
              Sign Up
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginForm;